import { createResponseSchema } from '@mint-sdk/api-core';
import { z } from 'zod/v4';

export const ApprovalItemsEnum = z.enum(['buying_plan', 'flight']);
export const ApprovalConnector = z.enum(['and', 'or', 'then']);
export const FlowStatusesEnum = z.enum([
  'active',
  'inactive',
  'scheduled',
  'ended',
]);
export type FlowStatus = z.infer<typeof FlowStatusesEnum>;

export const FlowListRequestSchema = z.object({
  options: z.array(
    z.object({
      value: z.object({
        type: z.enum(['approval_item']),
        approvalItems: z.array(ApprovalItemsEnum),
      }),
    }),
  ),
  sort: z.enum(['asc', 'desc', 'date']),
});

const RequestGroupUserVariantSchema = z.object({
  all: z.literal(false),
  id: z.string(),
  roles: z.null(),
  users: z.array(
    z.object({
      id: z.string(),
      userName: z.string(),
    }),
  ),
});

const RequestGroupRoleVariantSchema = z.object({
  all: z.literal(false),
  id: z.string(),
  roles: z.array(
    z.object({
      id: z.string(),
      role: z.string(),
    }),
  ),
  users: z.null(),
});

// TODO: deprecated option that should never appear in the approval v2 flow
const RequestGroupAllVariantSchema = z.object({
  all: z.literal(true),
  id: z.string(),
  roles: z.null(),
  users: z.null(),
});

const RequestGroupSchema = RequestGroupUserVariantSchema.or(
  RequestGroupRoleVariantSchema,
).or(RequestGroupAllVariantSchema);

const ApproveGroupUserVariantSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('users'),
  users: z.array(
    z.object({
      id: z.string(),
      userName: z.string(),
    }),
  ),
  role: z.null(),
  operation: z.null(),
});

const ApproveGroupRoleVariantSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('roles'),
  users: z.null(),
  role: z.object({
    id: z.string(),
    role: z.string(),
  }),
  operation: z.null(),
});

const ApproveGroupOperationVariantSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('operation'),
  users: z.null(),
  role: z.null(),
  operation: ApprovalConnector,
});

const ApproveGroupSchema = ApproveGroupUserVariantSchema.or(
  ApproveGroupRoleVariantSchema,
).or(ApproveGroupOperationVariantSchema);

const FlowItemLiteSchema = z.object({
  id: z.string(),
  name: z.string(),
  approvalItems: z.array(ApprovalItemsEnum),
  requestGroup: RequestGroupSchema,
  notificationPeriod: z.number().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  archived: z.boolean(),
  approval: z.array(ApproveGroupSchema),
  isEmailsForApprovedEntityEnabled: z.boolean(),
  emailsForApprovedEntity: z.array(z.email()),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  inUse: z.boolean(),
  canBeDisabled: z.boolean(),
});

export const FlowListResponseSchema = createResponseSchema(FlowItemLiteSchema);

const FlowItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  approvalItems: z.array(ApprovalItemsEnum),
  requestGroup: RequestGroupSchema,
  notificationPeriod: z.number().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  archived: z.boolean(),
  approval: z.array(ApproveGroupSchema),
  isEmailsForApprovedEntityEnabled: z.boolean(),
  emailsForApprovedEntity: z.array(z.email()),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  inUse: z.boolean(),
  canBeDisabled: z.boolean(),
});

export const FlowItemResponseSchema = createResponseSchema(FlowItemSchema);

const UserAssignee = z.object({
  userName: z.string(),
  role: z.string(),
  id: z.string(),
});

const RoleAssignee = z.object({
  userName: z.null(),
  role: z.string(),
  id: z.string(),
});

const UserSelection = z.object({
  requestBy: z.literal('users'),
  requestorsData: z.array(UserAssignee),
});

const RoleSelection = z.object({
  requestBy: z.literal('roles'),
  requestorsData: z.array(RoleAssignee),
});

const UserApprover = z.object({
  approveBy: z.literal('users'),
  nextApproveCondition: ApprovalConnector.nullable(),
  approversData: z.array(UserAssignee),
});

const RoleApprover = z.object({
  approveBy: z.literal('roles'),
  nextApproveCondition: ApprovalConnector.nullable(),
  approversData: z.array(RoleAssignee),
});

export const CreateFlowItemRequestSchema = z.object({
  flowType: ApprovalItemsEnum,
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  notifications: z.object({
    enabled: z.boolean(),
    notifyIn: z.number(),
  }),
  flowName: z.string(),
  requestor: z.union([UserSelection, RoleSelection]),
  approvers: z.array(z.union([UserApprover, RoleApprover])),
  isEmailsForApprovedEntityEnabled: z.boolean(),
  emailsForApprovedEntity: z.array(z.email()),
  notificationPeriod: z.number().nullable(),
});

export const UpdateFlowItemRequestSchema = z.object({
  id: z.string(),
  flowName: z.string(),
  requestor: z.intersection(
    z.object({ id: z.string() }),
    z.union([UserSelection, RoleSelection]),
  ),
  approvers: z.array(
    z.intersection(
      z.object({ id: z.string() }),
      z.union([UserApprover, RoleApprover]),
    ),
  ),
  notifications: z.object({
    enabled: z.boolean(),
    notifyIn: z.number(),
  }),

  notificationPeriod: z.number().nullable(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  isEmailsForApprovedEntityEnabled: z.boolean(),
  emailsForApprovedEntity: z.array(z.email()),
});

export const UpdateFlowItemStatusRequestSchema = z.object({
  archive: z.boolean(),
});

export type FlowItemLite = z.infer<typeof FlowItemLiteSchema>;
export type FlowListResponse = z.infer<typeof FlowListResponseSchema>;
export type FlowItem = z.infer<typeof FlowItemLiteSchema>;
export type FlowItemResponse = z.infer<typeof FlowListResponseSchema>;
export type CreateFlowItemRequest = z.infer<typeof CreateFlowItemRequestSchema>;
export type UpdateFlowItemRequest = z.infer<typeof UpdateFlowItemRequestSchema>;
